import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface ChatNotification {
  id: string;
  type: 'new_message' | 'new_conversation' | 'transfer' | 'assignment' | 'system';
  title: string;
  message: string;
  conversation_id?: string;
  client_name?: string;
  client_phone?: string;
  sender_name?: string;
  department?: string;
  read: boolean;
  created_at: string;
}

interface NotificationState {
  notifications: ChatNotification[];
  unreadCount: number;
  soundEnabled: boolean;
  browserEnabled: boolean;
  addNotification: (n: Omit<ChatNotification, 'id' | 'read' | 'created_at'>) => void;
  markAsRead: (id: string) => void;
  markAllRead: () => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  toggleSound: () => void;
  toggleBrowser: () => void;
}

export const useNotificationStore = create<NotificationState>()(
  persist(
    set => ({
      notifications: [],
      unreadCount: 0,
      soundEnabled: true,
      browserEnabled: true,
      addNotification: n =>
        set(state => {
          const notification: ChatNotification = {
            ...n,
            id: Date.now().toString(36) + Math.random().toString(36).slice(2, 7),
            read: false,
            created_at: new Date().toISOString(),
          };
          return {
            notifications: [notification, ...state.notifications].slice(0, 100),
            unreadCount: state.unreadCount + 1,
          };
        }),
      markAsRead: id =>
        set(state => {
          const target = state.notifications.find(notif => notif.id === id);
          if (!target || target.read) return state;
          return {
            notifications: state.notifications.map(notif =>
              notif.id === id ? { ...notif, read: true } : notif,
            ),
            unreadCount: Math.max(0, state.unreadCount - 1),
          };
        }),
      markAllRead: () =>
        set(state => ({
          notifications: state.notifications.map(notif => ({ ...notif, read: true })),
          unreadCount: 0,
        })),
      removeNotification: id =>
        set(state => {
          const target = state.notifications.find(notif => notif.id === id);
          return {
            notifications: state.notifications.filter(notif => notif.id !== id),
            unreadCount:
              target && !target.read ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
          };
        }),
      clearAll: () => set({ notifications: [], unreadCount: 0 }),
      toggleSound: () => set(state => ({ soundEnabled: !state.soundEnabled })),
      toggleBrowser: () => set(state => ({ browserEnabled: !state.browserEnabled })),
    }),
    {
      name: 'contachat-notifications',
      partialize: state => ({
        soundEnabled: state.soundEnabled,
        browserEnabled: state.browserEnabled,
      }),
    },
  ),
);
